import _isEmpty from "lodash/isEmpty";
import _pick from "lodash/pick";
import { AnyAction } from "redux";
import { VERIFICATION_TYPE } from "../../../../../constants";
import { Customer, LegalOwner } from "../../../../../models/state";
import * as CHECKOUT_AT from "../../../checkout/state/actionTypes";
import { fulfilled } from "../../utils";
import * as AT from "../actionTypes";

const initialState: Customer = {
    info: {
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        customerType: "",
        isSecretNumber: false,
        isCatalogListingOk: true,
        isProductUserDifferentThanLegalOwner: false,
        isOptingOutForTeliaXServices: false,
        isEmailTakenForTeliaTrygg: false,
        isEmailTakenForTeliaSky: false,
        isBarringsUsed: false,
        isCampaignAllowed: false,
        isCampaignToggled: false,
        isEsimSelected: false,
        isProductUserAgeOk: false,
        legalOwner: {},
    },
    legalOwner: {},
    existingSubscriptions: [],
    newSubscriptionIcims: {
        mhs: [],
        mbb: [],
        prepaid: [],
        vas: [],
    },
    verification: {
        creditCheck: {
            decision: undefined,
            message: "",
        },
        identification: {
            performed: false,
            shouldPoll: false,
        },
        manual: {
            performed: false,
        },
    },
};

const handleLegalOwnerFromCreditCheck = (state: Partial<LegalOwner>, action: AnyAction): Partial<LegalOwner> => {
    const { payload } = action;

    if (!_isEmpty(payload)) {
        return {
            ..._pick(action.payload, ["birthDate", "firstName", "lastName", "postcode", "city", "street"]),
            email: payload.contactEmail || payload.email,
            phone: payload.contactPhone,
        };
    }

    return state;
};

const retailerReducer = (state = initialState, action: AnyAction): Customer => {
    switch (action.type) {
        case fulfilled(AT.RETAILER_IDP_AUTHORIZE):
            if (action.payload.redirectUrl) {
                window.location.href = action.payload.redirectUrl;
            }
            return {
                ...state,
            };
        case AT.RETAILER_RESET_CUSTOMER_INFO:
            return {
                ...state,
                info: initialState.info,
            };
        case AT.RETAILER_SET_CUSTOMER_INFO_VALUE:
            return {
                ...state,
                info: {
                    ...state.info,
                    [action.field]: action.value,
                },
            };
        case fulfilled(AT.RETAILER_SET_CUSTOMER_DETAILS):
            return {
                ...state,
                info: {
                    ...state.info,
                    isEmailTakenForTeliaSky: action.payload.isEmailTakenForTeliaSky,
                    isEmailTakenForTeliaTrygg: action.payload.isEmailTakenForTeliaTrygg,
                },
            };
        case fulfilled(AT.RETAILER_PERFORM_CREDIT_CHECK): {
            if (action.payload.isProductUserLookup) {
                return state;
            }

            const isCustomerLookup = action.payload.isCreditCheckPerformed === false;
            const isCreditCheck = !!action.payload.creditCheckDecision;

            return {
                ...state,
                legalOwner: handleLegalOwnerFromCreditCheck(state.legalOwner, action),
                existingAccounts: action.payload.existingAccounts || [],
                selectedAccountId: action.payload.selectedAccountId,
                existingPhoneNumberLookup: action.payload.existingPhoneNumberLookup,
                contactInfo: {
                    email: action.payload.contactEmail ?? action.payload.email,
                    phone: action.payload.contactPhone ?? action.payload.phone,
                },
                verification: {
                    ...state.verification,
                    creditCheck: {
                        requireManuallyAddress: !!action.payload.requireManuallyAddress,
                        decision: action.payload.creditCheckDecision,
                        isDOrTNumber: !!action.payload.isDOrTNumber,
                        leaseRestrictionAmount: action.payload.leaseRestrictionAmount,
                        message: action.payload.creditCheckMessage,
                        performed: isCreditCheck,
                    },
                    manual: {
                        performed: isCustomerLookup,
                        result: action.payload.manualResult,
                    },
                },
            };
        }
        case fulfilled(AT.RETAILER_SET_CONTACT_INFO):
            return {
                ...state,
                legalOwner: {
                    ...state.legalOwner,
                    email: action.payload.email,
                    phone: action.payload.phone || state.legalOwner?.phone,
                },
                contactInfo: {
                    email: action.payload.email,
                    phone: action.payload.phone,
                },
            };
        case fulfilled(AT.RETAILER_SET_IDENTIFICATION):
            return {
                ...state,
                verification: {
                    ...state.verification,
                    identification: {
                        performed: action.payload.type === VERIFICATION_TYPE.BANK_ID ? false : action.payload.success,
                        type: action.payload.type,
                        shouldPoll: action.payload.shouldPoll,
                    },
                },
            };
        case fulfilled(AT.RETAILER_GET_BANKID_RESULT):
            return {
                ...state,
                verification: {
                    ...state.verification,
                    identification: {
                        ...state.verification?.identification,
                        performed: !!action.payload.success,
                        shouldPoll: !action.payload.success,
                        customer: action.payload.success ? action.payload.customer : undefined,
                    },
                },
            };
        case AT.RETAILER_CANCEL_BANKID_POLLING:
            return {
                ...state,
                verification: {
                    ...state.verification,
                    identification: {
                        ...state.verification?.identification,
                        shouldPoll: false,
                    },
                },
            };
        case fulfilled(AT.RETAILER_SET_EXISTING_SUBSCRIPTION_FOR_CHANGE):
            return {
                ...state,
                existingSubscriptionForChange: action.payload,
            };
        case AT.RETAILER_SET_REOPEN_EXISTING_SUBSCRIPTION_PHONE:
            return {
                ...state,
                reopenExistingSubscriptionPhone: action.payload.phoneNumber,
            };
        case AT.RETAILER_CLEAR_REOPEN_EXISTING_SUBSCRIPTION_PHONE:
            return {
                ...state,
                reopenExistingSubscriptionPhone: undefined,
            };
        case AT.RETAILER_CLEAR_EXISTING_SUBSCRIPTION_FOR_CHANGE:
        case fulfilled(CHECKOUT_AT.CHECKOUT_REMOVE_ITEM_BY_CART_ITEM_ID_FROM_CART):
        case fulfilled(CHECKOUT_AT.CHECKOUT_REMOVE_ITEM_BY_ID_FROM_CART):
            return {
                ...state,
                existingSubscriptionForChange: undefined,
            };
        case fulfilled(AT.RETAILER_LOOKUP_CUSTOMER_BY_PHONE_NUMBER):
            return {
                ...initialState,
            };
        case AT.RETAILER_SET_SELECTED_ACCOUNT_ID:
            return {
                ...state,
                selectedAccountId: action.accountId,
            };
        default:
            return state;
    }
};

export default retailerReducer;
